// ============================================================
// src/services/AuditService.js
// GAP-SEC-001 — Audit Logging — Complete Service
// Branch: GAP · Mar 22, 2026
// ─────────────────────────────────────────────────────────────
// GAP-DATA-001 CHANGES (Mar 30, 2026):
//   - CLIENT_ARCHIVE:   'client.archive'   added to AUDIT_ACTIONS
//   - CLIENT_UNARCHIVE: 'client.unarchive' added to AUDIT_ACTIONS
//   - SEVERITY_MAP: CLIENT_ARCHIVE → CRITICAL, CLIENT_UNARCHIVE → WARNING
//   - COVERAGE header updated
// ============================================================
//
// SCHEMA ALIGNMENT:
//   Existing auditLogs documents have: action, metadata, createdAt, timestamp
//   This service EXTENDS that shape — fully backward-compatible.
//   Old fields retained:   action, metadata, createdAt, timestamp
//   New fields added:      module, severity, actorId, actorName, actorRole,
//                          firmId, targetId, targetType, targetName, changes
//
// WHY actorName?
//   actorId alone is unreadable in the AuditLogViewer.
//   actorName is denormalized at write time from the user doc / AuthContext.
//   This is intentional — audit logs must be self-contained.
//   If a user is later deleted, the log still shows who did it.
//
// COVERAGE — every app operation that writes data must call log():
//   ✅ Auth              login, logout, session expiry, force logout
//   ✅ User Management   create, update, role change, deactivate, delete
//   ✅ Case Management   create, update, status change, archive, unarchive, assign, notes, docs
//   ✅ Client / Lead     create, update, archive, unarchive, lead approve/reject, portal link
//   ✅ Invoice           create, void, cancel
//   ✅ Payment           record, cheque cleared/bounced
//   ✅ Draft Library     upload, assign, remove, delete, download
//   ✅ Calendar/Events   create, update, delete (case events + general meetings)
//   ✅ Firm Settings     settings update, rate update
//   ✅ Import Jobs       started, completed, failed
//
// DESIGN RULES:
//   1. NEVER throw — audit failure must NEVER block the primary operation
//   2. actorId + actorName + actorRole all required — no anonymous logs
//   3. serverTimestamp() for timestamp — never client new Date() for ordering
//   4. createdAt (ISO string) kept for backward compat with existing documents
//   5. All callers use AUDIT_ACTIONS constants — no raw strings
//   6. metadata object kept for backward compat + extra structured data
//
// ============================================================

import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';

// ─── Audit Action Catalog ────────────────────────────────────────────────────
//
// Format: module.action  (dot-separated, all lowercase)
// ALL callers MUST use these constants — no raw strings anywhere.
// The module prefix (before the dot) is used as a filter key in AuditLogViewer.
//
export const AUDIT_ACTIONS = {

  // ── Auth ──────────────────────────────────────────────────────────────────
  AUTH_LOGIN:                   'auth.login',
  AUTH_LOGOUT:                  'auth.logout',
  AUTH_SESSION_EXPIRED:         'auth.sessionExpired',
  AUTH_FORCE_LOGOUT:            'auth.forceLogout',          // firm mismatch, admin kick

  // ── User Management ───────────────────────────────────────────────────────
  USER_CREATE:                  'user.create',
  USER_UPDATE:                  'user.update',
  USER_ROLE_CHANGE:             'user.roleChange',
  USER_DEACTIVATE:              'user.deactivate',
  USER_REACTIVATE:              'user.reactivate',
  USER_DELETE:                  'user.delete',
  USER_INVITE_SENT:             'user.inviteSent',
  USER_PASSWORD_RESET:          'user.passwordReset',
  USER_PHOTO_UPLOAD:            'user.photoUpload',

  // ── Case Management ───────────────────────────────────────────────────────
  CASE_CREATE:                  'case.create',
  CASE_UPDATE:                  'case.update',
  CASE_STATUS_CHANGE:           'case.statusChange',
  CASE_ASSIGN:                  'case.assign',
  CASE_ARCHIVE:                 'case.archive',
  CASE_UNARCHIVE:               'case.unarchive',
  CASE_DELETE:                  'case.delete',
  CASE_NOTE_ADD:                'case.noteAdd',
  CASE_NOTE_DELETE:             'case.noteDelete',
  CASE_DOCUMENT_UPLOAD:         'case.documentUpload',
  CASE_DOCUMENT_DELETE:         'case.documentDelete',

  // ── Lead Workflow ─────────────────────────────────────────────────────────
  LEAD_CREATE:                  'lead.create',
  LEAD_APPROVE:                 'lead.approve',
  LEAD_REJECT:                  'lead.reject',

  // ── Client Management ─────────────────────────────────────────────────────
  CLIENT_CREATE:                'client.create',
  CLIENT_UPDATE:                'client.update',
  CLIENT_DELETE:                'client.delete',
  CLIENT_PORTAL_LINK_SENT:      'client.portalLinkSent',
  CLIENT_PORTAL_ACCESS_REVOKED: 'client.portalAccessRevoked',
  // ✅ GAP-DATA-001 (Mar 30, 2026): Archive actions for clients
  CLIENT_ARCHIVE:               'client.archive',
  CLIENT_UNARCHIVE:             'client.unarchive',

  // ── Invoice Management ────────────────────────────────────────────────────
  INVOICE_CREATE:               'invoice.create',
  INVOICE_VOID:                 'invoice.void',
  INVOICE_CANCEL:               'invoice.cancel',

  // ── Payment Management ────────────────────────────────────────────────────
  PAYMENT_RECORD:               'payment.record',
  PAYMENT_CHEQUE_CLEARED:       'payment.chequeCleared',
  PAYMENT_CHEQUE_BOUNCED:       'payment.chequeBounced',

  // ── Draft Library ─────────────────────────────────────────────────────────
  DRAFT_UPLOAD:                 'draft.upload',
  DRAFT_ASSIGN:                 'draft.assignToCase',
  DRAFT_REMOVE:                 'draft.removeFromCase',
  DRAFT_DELETE:                 'draft.delete',
  DRAFT_DOWNLOAD:               'draft.download',

  // ── Calendar & Events ─────────────────────────────────────────────────────
  EVENT_CREATE:                 'event.create',
  EVENT_UPDATE:                 'event.update',
  EVENT_DELETE:                 'event.delete',
  MEETING_CREATE:               'meeting.create',
  MEETING_UPDATE:               'meeting.update',
  MEETING_DELETE:               'meeting.delete',

  // ── Team Chat ─────────────────────────────────────────────────────────────
  // High volume — only log if compliance requires it
  CHAT_MESSAGE_DELETED:         'chat.messageDeleted',

  // ── Firm Settings ─────────────────────────────────────────────────────────
  FIRM_SETTINGS_UPDATE:         'firm.settingsUpdate',
  FIRM_RATE_UPDATE:             'firm.rateUpdate',

  // ── Import Jobs ───────────────────────────────────────────────────────────
  IMPORT_STARTED:               'import.started',
  IMPORT_COMPLETED:             'import.completed',
  IMPORT_FAILED:                'import.failed',
};

// ─── Severity Levels ─────────────────────────────────────────────────────────
export const AUDIT_SEVERITY = {
  INFO:     'info',     // Normal operation — create, view, assign
  WARNING:  'warning',  // Reversible sensitive op — status change, edit
  CRITICAL: 'critical', // Destructive / high-risk — delete, void, deactivate
};

// ─── Auto-severity map ────────────────────────────────────────────────────────
// Callers don't specify severity — it's derived automatically from the action.
const SEVERITY_MAP = {
  [AUDIT_ACTIONS.USER_DELETE]:                  AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.USER_DEACTIVATE]:              AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.USER_ROLE_CHANGE]:             AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.CASE_ARCHIVE]:                 AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.CASE_DELETE]:                  AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.CASE_DOCUMENT_DELETE]:         AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.CLIENT_DELETE]:                AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.CLIENT_ARCHIVE]:               AUDIT_SEVERITY.CRITICAL,  // ✅ GAP-DATA-001
  [AUDIT_ACTIONS.CLIENT_PORTAL_ACCESS_REVOKED]: AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.INVOICE_VOID]:                 AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.INVOICE_CANCEL]:               AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.PAYMENT_CHEQUE_BOUNCED]:       AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.AUTH_FORCE_LOGOUT]:            AUDIT_SEVERITY.CRITICAL,
  [AUDIT_ACTIONS.DRAFT_DELETE]:                 AUDIT_SEVERITY.CRITICAL,

  [AUDIT_ACTIONS.CASE_UPDATE]:                  AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.CASE_STATUS_CHANGE]:           AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.CASE_ASSIGN]:                  AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.CASE_UNARCHIVE]:               AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.CASE_NOTE_DELETE]:             AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.CLIENT_UNARCHIVE]:             AUDIT_SEVERITY.WARNING,   // ✅ GAP-DATA-001
  [AUDIT_ACTIONS.LEAD_APPROVE]:                 AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.LEAD_REJECT]:                  AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.CLIENT_UPDATE]:                AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.INVOICE_CREATE]:               AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.PAYMENT_RECORD]:               AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.PAYMENT_CHEQUE_CLEARED]:       AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.FIRM_SETTINGS_UPDATE]:         AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.FIRM_RATE_UPDATE]:             AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.USER_UPDATE]:                  AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.EVENT_DELETE]:                 AUDIT_SEVERITY.WARNING,
  [AUDIT_ACTIONS.MEETING_DELETE]:               AUDIT_SEVERITY.WARNING,
};

// ─── Core log() writer ───────────────────────────────────────────────────────
//
// @param {string}  firmId       required
// @param {string}  action       required — AUDIT_ACTIONS constant
// @param {string}  actorId      required — Firebase UID
// @param {string}  actorName    required — display name e.g. "Priya Sharma"
// @param {string}  actorRole    required — role at time of action e.g. "Partner"
// @param {object}  [extra={}]   optional context:
//                                 targetId, targetType, targetName,
//                                 changes { before, after }, note,
//                                 + any extra fields → go into metadata{}
//
async function log(firmId, action, actorId, actorName, actorRole, extra = {}) {
  if (!firmId || !action || !actorId || !actorName || !actorRole) {
    console.warn('[AuditService] Skipped — missing required field(s):', {
      firmId: !!firmId, action, actorId: !!actorId,
      actorName: !!actorName, actorRole,
    });
    return false;
  }

  const severity = extra.severity ?? SEVERITY_MAP[action] ?? AUDIT_SEVERITY.INFO;
  const { targetId, targetType, targetName, changes, note, severity: _s, ...rest } = extra;

  try {
    await addDoc(collection(db, 'firms', firmId, 'auditLogs'), {
      // ── Backward-compatible (existing schema) ─────────────────────────────
      action,
      timestamp:  serverTimestamp(),         // authoritative — always server time
      createdAt:  new Date().toISOString(),  // ISO string — backward compat

      // ── New structured fields ─────────────────────────────────────────────
      module:     action.split('.')[0],      // "case" — enables single-field filter
      severity,
      firmId,

      // ── Actor ─────────────────────────────────────────────────────────────
      actorId,                               // UID — for programmatic lookup
      actorName,                             // "Priya Sharma" — readable display
      actorRole,                             // "Partner" — role at time of action

      // ── Target ────────────────────────────────────────────────────────────
      targetId:   targetId   ?? null,
      targetType: targetType ?? null,
      targetName: targetName ?? null,
      changes:    changes    ?? null,        // { before: {}, after: {} }
      note:       note       ?? null,

      // ── metadata (backward compat object + any extra structured fields) ───
      metadata:   Object.keys(rest).length > 0 ? rest : {},
    });

    return true;

  } catch (err) {
    // NEVER re-throw — audit failure must not block primary operation
    console.error('[AuditService] Write failed:', { action, firmId, actorId, err: err.message });
    return false;
  }
}

// ─── Domain wrappers ─────────────────────────────────────────────────────────
//
// Uniform signature for all wrappers:
//   (firmId, action, actorId, actorName, actorRole, meta?)
//
// Always import and use these in service files — not log() directly.
//

/** Auth events — called from AuthContext.js */
async function logAuth(firmId, action, actorId, actorName, actorRole, meta = {}) {
  return log(firmId, action, actorId, actorName, actorRole, { targetType: 'auth', ...meta });
}

/** User management — called from UserService.js */
async function logUser(firmId, action, actorId, actorName, actorRole, meta = {}) {
  return log(firmId, action, actorId, actorName, actorRole, { targetType: 'user', ...meta });
}

/** Case management — called from CaseService.js */
async function logCase(firmId, action, actorId, actorName, actorRole, meta = {}) {
  return log(firmId, action, actorId, actorName, actorRole, { targetType: 'case', ...meta });
}

/** Lead / client — called from ClientService.js */
async function logClient(firmId, action, actorId, actorName, actorRole, meta = {}) {
  return log(firmId, action, actorId, actorName, actorRole, {
    targetType: action.startsWith('lead.') ? 'lead' : 'client',
    ...meta,
  });
}

/** Invoices — called from InvoiceService.js */
async function logInvoice(firmId, action, actorId, actorName, actorRole, meta = {}) {
  return log(firmId, action, actorId, actorName, actorRole, { targetType: 'invoice', ...meta });
}

/** Payments — called from PaymentService.js */
async function logPayment(firmId, action, actorId, actorName, actorRole, meta = {}) {
  return log(firmId, action, actorId, actorName, actorRole, { targetType: 'payment', ...meta });
}

/** Draft library — called from draft handlers */
async function logDraft(firmId, action, actorId, actorName, actorRole, meta = {}) {
  return log(firmId, action, actorId, actorName, actorRole, { targetType: 'draft', ...meta });
}

/** Calendar events & meetings */
async function logEvent(firmId, action, actorId, actorName, actorRole, meta = {}) {
  return log(firmId, action, actorId, actorName, actorRole, {
    targetType: action.startsWith('meeting.') ? 'meeting' : 'event',
    ...meta,
  });
}

/** Firm settings */
async function logFirm(firmId, action, actorId, actorName, actorRole, meta = {}) {
  return log(firmId, action, actorId, actorName, actorRole, { targetType: 'firm', ...meta });
}

// ─── Reader — for AuditLogsPage.jsx ──────────────────────────────────────────
//
// Returns { logs: [], lastDoc: cursor|null }
// Supports: module, actorId, severity, action filters + pagination
//
async function fetchLogs(firmId, filters = {}, pageSize = 50, lastDoc = null) {
  try {
    const constraints = [orderBy('timestamp', 'desc'), limit(pageSize)];
    if (filters.module)   constraints.unshift(where('module',   '==', filters.module));
    if (filters.actorId)  constraints.unshift(where('actorId',  '==', filters.actorId));
    if (filters.severity) constraints.unshift(where('severity', '==', filters.severity));
    if (filters.action)   constraints.unshift(where('action',   '==', filters.action));
    if (lastDoc)          constraints.push(startAfter(lastDoc));

    const snap = await getDocs(query(collection(db, 'firms', firmId, 'auditLogs'), ...constraints));
    return {
      logs:    snap.docs.map(d => ({ id: d.id, ...d.data() })),
      lastDoc: snap.docs[snap.docs.length - 1] ?? null,
    };
  } catch (err) {
    console.error('[AuditService] fetchLogs failed:', err);
    return { logs: [], lastDoc: null };
  }
}

// ─── Export ───────────────────────────────────────────────────────────────────

const auditService = {
  log,
  logAuth, logUser, logCase, logClient,
  logInvoice, logPayment, logDraft, logEvent, logFirm,
  fetchLogs,
};

export default auditService;